import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BookOpen, Play, Clock, CreditCard, MessageCircle, Send, User, LogOut, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.1 } })
};

export default function DashboardPage() {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [dashData, setDashData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user === false) { navigate('/login'); return; }
    axios.get(`${API}/dashboard`, { withCredentials: true })
      .then(res => { setDashData(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [user, authLoading, navigate]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const sendChat = async (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    const msg = chatInput;
    setChatMessages(prev => [...prev, { role: 'user', text: msg }]);
    setChatInput('');
    setChatLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat`, { message: msg, session_id: sessionId }, { withCredentials: true });
      setChatMessages(prev => [...prev, { role: 'assistant', text: data.response }]);
      if (data.session_id) setSessionId(data.session_id);
    } catch {
      setChatMessages(prev => [...prev, { role: 'assistant', text: 'Sorry, I could not process your request. Please try again.' }]);
    }
    setChatLoading(false);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="animate-pulse text-slate-400">Loading dashboard...</div>
      </div>
    );
  }

  const courses = dashData?.courses || [];
  const transactions = dashData?.transactions || [];

  return (
    <div data-testid="dashboard-page" className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Welcome */}
        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="mb-10">
          <h1 className="font-serif text-4xl sm:text-5xl text-slate-900 mb-2">Welcome back, {user?.name}</h1>
          <p className="text-slate-500">Continue your transformation journey</p>
        </motion.div>

        <Tabs defaultValue="courses" className="space-y-8">
          <TabsList className="bg-white border border-slate-200 rounded-full p-1" data-testid="dashboard-tabs">
            <TabsTrigger value="courses" className="rounded-full data-[state=active]:bg-slate-900 data-[state=active]:text-white px-6" data-testid="tab-courses">
              <BookOpen className="w-4 h-4 mr-2" /> My Courses
            </TabsTrigger>
            <TabsTrigger value="ai-coach" className="rounded-full data-[state=active]:bg-slate-900 data-[state=active]:text-white px-6" data-testid="tab-ai-coach">
              <MessageCircle className="w-4 h-4 mr-2" /> AI Coach
            </TabsTrigger>
            <TabsTrigger value="transactions" className="rounded-full data-[state=active]:bg-slate-900 data-[state=active]:text-white px-6" data-testid="tab-transactions">
              <CreditCard className="w-4 h-4 mr-2" /> Transactions
            </TabsTrigger>
          </TabsList>

          {/* Courses Tab */}
          <TabsContent value="courses">
            {courses.length === 0 ? (
              <motion.div variants={fadeUp} initial="hidden" animate="visible" className="bg-white rounded-2xl border border-slate-100 p-12 text-center">
                <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-serif text-xl text-slate-900 mb-2">No Courses Yet</h3>
                <p className="text-slate-500 mb-6">Start your transformation by enrolling in a program.</p>
                <Link to="/courses">
                  <Button className="rounded-full bg-slate-900 text-white gap-2" data-testid="explore-courses-btn">
                    Explore Programs <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </motion.div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {courses.map((course, i) => (
                  <motion.div key={course.id} variants={fadeUp} initial="hidden" animate="visible" custom={i}>
                    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden" data-testid={`dashboard-course-${course.id}`}>
                      <div className="h-40 overflow-hidden">
                        <img src={course.image} alt={course.title} className="w-full h-full object-cover" />
                      </div>
                      <div className="p-6">
                        <h3 className="font-serif text-xl font-semibold text-slate-900 mb-1">{course.title}</h3>
                        <div className="flex items-center gap-3 text-sm text-slate-500 mb-4">
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{course.duration}</span>
                          <span>{course.modules} Modules</span>
                        </div>
                        {/* Video List */}
                        <div className="space-y-2">
                          {course.videos?.map((v, vi) => (
                            <div key={vi} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer" data-testid={`video-${course.id}-${vi}`}>
                              <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                                <Play className="w-4 h-4 text-indigo-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-900 truncate">{v.title}</p>
                                <p className="text-xs text-slate-500">{v.duration}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* AI Coach Tab */}
          <TabsContent value="ai-coach">
            <motion.div variants={fadeUp} initial="hidden" animate="visible" className="bg-white rounded-2xl border border-slate-100 overflow-hidden" data-testid="ai-coach-section">
              <div className="p-6 border-b border-slate-100">
                <h3 className="font-serif text-xl font-semibold text-slate-900">AI Manifestation Coach</h3>
                <p className="text-sm text-slate-500 mt-1">Ask questions about manifestation, mindset, abundance, and personal growth.</p>
              </div>
              <div className="h-[400px] overflow-y-auto p-6 space-y-4" data-testid="chat-messages">
                {chatMessages.length === 0 && (
                  <div className="text-center py-12">
                    <MessageCircle className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                    <p className="text-sm text-slate-400">Start a conversation with your AI coach</p>
                    <div className="flex flex-wrap gap-2 justify-center mt-4">
                      {['How do I manifest abundance?', 'Tips for morning routine', 'Help with money blocks'].map((q, i) => (
                        <button
                          key={i}
                          onClick={() => { setChatInput(q); }}
                          className="text-xs px-3 py-2 rounded-full bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors"
                          data-testid={`chat-suggestion-${i}`}
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {chatMessages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${
                      msg.role === 'user'
                        ? 'bg-slate-900 text-white'
                        : 'bg-slate-100 text-slate-900'
                    }`} data-testid={`chat-message-${i}`}>
                      {msg.text}
                    </div>
                  </div>
                ))}
                {chatLoading && (
                  <div className="flex justify-start">
                    <div className="bg-slate-100 rounded-2xl px-4 py-3 text-sm text-slate-500 animate-pulse">Thinking...</div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
              <form onSubmit={sendChat} className="p-4 border-t border-slate-100 flex gap-3" data-testid="chat-form">
                <Input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder="Ask your AI coach anything..."
                  className="flex-1"
                  disabled={chatLoading}
                  data-testid="chat-input"
                />
                <Button type="submit" disabled={chatLoading || !chatInput.trim()} className="rounded-full bg-slate-900 text-white" data-testid="chat-send-btn">
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </motion.div>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <motion.div variants={fadeUp} initial="hidden" animate="visible" className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
              {transactions.length === 0 ? (
                <div className="p-12 text-center">
                  <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-serif text-xl text-slate-900 mb-2">No Transactions Yet</h3>
                  <p className="text-slate-500">Your purchase history will appear here.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="transactions-table">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Course</th>
                        <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Amount</th>
                        <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Method</th>
                        <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                        <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((tx, i) => (
                        <tr key={i} className="border-b border-slate-50">
                          <td className="p-4 text-sm font-medium text-slate-900">{tx.course_id}</td>
                          <td className="p-4 text-sm text-slate-600">{tx.currency === 'INR' ? `Rs.${tx.amount}` : `$${tx.amount}`}</td>
                          <td className="p-4 text-sm text-slate-600 capitalize">{tx.payment_method}</td>
                          <td className="p-4">
                            <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                              tx.payment_status === 'paid' ? 'bg-emerald-50 text-emerald-700' :
                              tx.payment_status === 'initiated' ? 'bg-amber-50 text-amber-700' :
                              'bg-red-50 text-red-700'
                            }`}>{tx.payment_status}</span>
                          </td>
                          <td className="p-4 text-sm text-slate-500">{new Date(tx.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </motion.div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
